export interface IntelligenceConversationSummary {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  pinned: boolean;
}

interface IntelligenceToolCall {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  outcome: string | null;
}

export interface IntelligenceAttachmentRef {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
}

export interface IntelligenceMessageNode {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  parentId: string | null;
  attachments: IntelligenceAttachmentRef[];
  cancelled: boolean;
  errored: boolean;
  toolCalls: IntelligenceToolCall[];
}

interface IntelligenceActiveTurn {
  runId: string;
  userNodeId: string;
  kind: string;
}

export interface IntelligenceConversation {
  id: string;
  title: string | null;
  messages: IntelligenceMessageNode[];
  currentNodeId: string | null;
  activeTurn: IntelligenceActiveTurn | null;
  createdAt: string;
  updatedAt: string;
  locale: string;
}

function utcInstant(value: unknown): string {
  if (typeof value !== "string" || value === "") return "";
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function asCount(value: unknown): number {
  const count = typeof value === "number" ? value : Number(value);
  return Number.isFinite(count) ? count : 0;
}

function asFlag(value: unknown): boolean {
  return value === true;
}

function toAttachment(raw: unknown): IntelligenceAttachmentRef | null {
  if (raw === null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = asString(record.id);
  if (!id) return null;
  return {
    id,
    filename: asString(record.filename),
    mimeType: asString(record.mime_type),
    size: asCount(record.size),
  };
}

function toToolCall(raw: unknown): IntelligenceToolCall | null {
  if (raw === null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const toolCallId = asString(record.tool_call_id);
  if (!toolCallId) return null;
  return {
    toolCallId,
    toolName: asString(record.tool_name),
    args:
      record.args !== null && typeof record.args === "object" && !Array.isArray(record.args)
        ? (record.args as Record<string, unknown>)
        : {},
    outcome: asNullableString(record.outcome),
  };
}

function toMessageNode(raw: unknown): IntelligenceMessageNode | null {
  if (raw === null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = asString(record.id);
  if (!id) return null;
  return {
    id,
    role: asString(record.role),
    content: asString(record.content),
    createdAt: utcInstant(record.created_at),
    parentId: asNullableString(record.parent_id),
    attachments: (Array.isArray(record.attachments) ? record.attachments : [])
      .map(toAttachment)
      .filter((attachment): attachment is IntelligenceAttachmentRef => attachment !== null),
    cancelled: asFlag(record.cancelled),
    errored: asFlag(record.errored),
    toolCalls: (Array.isArray(record.tool_calls) ? record.tool_calls : [])
      .map(toToolCall)
      .filter((toolCall): toolCall is IntelligenceToolCall => toolCall !== null),
  };
}

function toActiveTurn(raw: unknown): IntelligenceActiveTurn | null {
  if (raw === null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const runId = asString(record.run_id);
  if (!runId) return null;
  return {
    runId,
    userNodeId: asString(record.user_node_id),
    kind: asString(record.kind),
  };
}

export function toConversation(raw: unknown): IntelligenceConversation | null {
  if (raw === null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = asString(record.id);
  if (!id) return null;
  return {
    id,
    title: asNullableString(record.title),
    messages: (Array.isArray(record.messages) ? record.messages : [])
      .map(toMessageNode)
      .filter((message): message is IntelligenceMessageNode => message !== null),
    currentNodeId: asNullableString(record.current_node_id),
    activeTurn: toActiveTurn(record.active_turn),
    createdAt: utcInstant(record.created_at),
    updatedAt: utcInstant(record.updated_at),
    locale: asString(record.locale) || "sv",
  };
}

export function conversationFromBody(text: string): IntelligenceConversation | null {
  if (!text) return null;
  try {
    return toConversation(JSON.parse(text));
  } catch {
    return null;
  }
}

export function toConversationSummaries(raw: unknown): IntelligenceConversationSummary[] {
  return (Array.isArray(raw) ? raw : []).flatMap((row) => {
    if (row === null || typeof row !== "object") return [];
    const record = row as Record<string, unknown>;
    const id = asString(record.id);
    if (!id) return [];
    return [
      {
        id,
        title: asNullableString(record.title),
        createdAt: utcInstant(record.created_at),
        updatedAt: utcInstant(record.updated_at),
        messageCount: asCount(record.message_count),
        pinned: asFlag(record.pinned),
      },
    ];
  });
}

function errorCodeOf(text: string): string | null {
  if (!text) return null;
  try {
    const body = JSON.parse(text) as { code?: unknown };
    return typeof body.code === "string" && body.code !== "" ? body.code : null;
  } catch {
    return null;
  }
}

function upstreamFault(text: string): { status: number; code: string | null } | null {
  if (!text) return null;
  let message: unknown;
  try {
    message = (JSON.parse(text) as { message?: unknown }).message;
  } catch {
    return null;
  }
  if (typeof message !== "string") return null;
  const status = /\bstatusCode=(\d{3})\b/.exec(message);
  if (!status) return null;
  const code = /"code"\s*:\s*"([^"]+)"/.exec(message);
  return { status: Number(status[1]), code: code ? code[1] : null };
}

export function resolvedFault(
  status: number,
  body: string,
): { status: number; code: string | null } {
  const fault = upstreamFault(body);
  return fault ?? { status, code: errorCodeOf(body) };
}

export function createConversationBody(input: {
  title?: string;
  locale?: "sv" | "en";
}): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.locale !== undefined) body.locale = input.locale;
  return body;
}

export function patchConversationBody(patch: { title?: string | null }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if ("title" in patch) body.title = patch.title;
  return body;
}

export interface PostMessageInput {
  content: string;
  parentId?: string | null;
  locale?: "sv" | "en";
  attachmentIds?: string[];
  frontendContext?: object;
  tools?: object[];
}

export function postMessageBody(input: PostMessageInput): Record<string, unknown> {
  const body: Record<string, unknown> = { content: input.content };
  if ("parentId" in input) body.parent_id = input.parentId;
  if (input.locale !== undefined) body.locale = input.locale;
  if (input.attachmentIds !== undefined) body.attachment_ids = input.attachmentIds;
  if (input.frontendContext !== undefined) body.frontend_context = input.frontendContext;
  if (input.tools !== undefined && input.tools.length > 0) body.tools = input.tools;
  return body;
}
